import { useState, useRef } from "react";
import {
  Col,
  Row,
  Form,
  Tooltip,
  Dropdown,
  OverlayTrigger,
} from "react-bootstrap";
import Icon from "../generic/Icon";
import { useSiteContext } from "./Providers/SSCProvider";
import { useRoleContext } from "./Providers/RoleProvider";
import ExportSiteCircuitsCSV from "./ExportSiteCircuitsCSV";
import AddTempStructureModal from "./Modals/AddTempStructureModal";
import CancelModal from "./Modals/CancelModal";
import useApi from "../../apiProvider";
import MoveCircuitsModal from "./Modals/MoveCircuitsModal";

const ActionButtons = () => {
  const {
    selectedSite,
    isLoading,
    filtering,
    resetSite,
    hasChanges,
    setIsLoading,
    validationError,
    globalSitesData,
    buildSavePayload,
    rebuildHierarchy,
    selectEmptyStructure,
    handleFilteringSelect,
    selectedEmptyStructure,
    showEmptyStructures,
    setShowEmptyStructures,
    handleHideEmptyStructures,
    removeTempCircuit,
  } = useSiteContext();
  const exportRef = useRef();
  const { isAdmin } = useRoleContext();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCircuitsModal, setShowCircuitsModal] = useState(false);

  const handleShowCircuitModal = () => {
    handleHideEmptyStructures();
    setShowCircuitsModal(true);
  };

  const [tempStructureModal, setTempStructureModal] = useState({
    show: false,
    structureId: "",
    circuits: [],
  });

  const handleExport = (isAdmin, dataToExport) => {
    if (exportRef.current) {
      exportRef.current.exportToCSV(isAdmin, dataToExport);
    }
  };

  const handleSave = async () => {
    try {
      if (!selectedSite) return;
      setIsLoading(true);
      const payload = buildSavePayload();
      console.log("payload: ", payload);
      const results = await useApi.updateSiteData(payload);
      console.log("result: ", results);
      rebuildHierarchy({
        sites: results?.returnData.siteData,
        structures: results?.returnData.structureData,
        circuits: results?.returnData.circuitData,
        groups: results?.returnData.diversityGroupData,
        assignments: results?.returnData.diversityGroupAssignmentData,
      });
    } catch (e) {
      console.error("Save failed", e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Row className="ps-2" style={{ marginTop: "auto" }}>
        {showEmptyStructures && isAdmin ? (
          <Col
            className="d-flex justify-content-start align-items-center"
            md={7}
          >
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip-filter">
                  Click for Empty Structures
                </Tooltip>
              }
            >
              <Form.Select
                className={selectedEmptyStructure ? "no-caret" : ""}
                value={selectedEmptyStructure?.structureId || ""}
                onChange={(e) =>
                  selectEmptyStructure(Number(e.target.value), null)
                }
                style={{ paddingRight: "2rem" }}
              >
                {selectedEmptyStructure === null && (
                  <option disabled value="" style={{ fontStyle: "italic" }}>
                    (
                    {
                      selectedSite.structures?.filter(
                        (s) => s.circuits?.length === 0,
                      )?.length
                    }
                    ) Empty Structure
                    {selectedSite.structures?.filter(
                      (s) => s.circuits?.length === 0,
                    )?.length > 1
                      ? "s"
                      : ""}
                  </option>
                )}

                {selectedEmptyStructure &&
                  !selectedSite.structures.find(
                    (s) =>
                      s.structureId === selectedEmptyStructure.structureId &&
                      s.circuits?.length === 0,
                  ) && (
                    <option value={selectedEmptyStructure.structureId}>
                      {selectedEmptyStructure.structureId} -{" "}
                      {selectedEmptyStructure.structureName}
                    </option>
                  )}

                {selectedSite.structures
                  .filter((s) => s.circuits?.length === 0)
                  .sort((a, b) => a.structureId - b.structureId)
                  .map((structure) => (
                    <option
                      title="Click to Add a Circuit"
                      key={structure.structureId}
                      value={structure.structureId}
                    >
                      {structure.structureId} - {structure.structureName}
                    </option>
                  ))}
              </Form.Select>
            </OverlayTrigger>

            <div style={{ position: "relative" }}>
              {selectedEmptyStructure && (
                <Icon
                  name={"close"}
                  onClick={() =>
                    removeTempCircuit(selectedEmptyStructure.structureId)
                  }
                  overrides={{
                    tooltip: "Discard Empty Structure Changes",
                    tooltipPlacement: "bottom",
                    iconColor: showEmptyStructures && "#E02827",
                    style: {
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                  }}
                  id="close-icon"
                />
              )}
            </div>
          </Col>
        ) : (
          <Col md={7} />
        )}
        <Col
          className="d-flex justify-content-end align-items-center"
          md={5}
          style={{ height: "40px" }}
        >
          <div
            className="d-flex align-items-center flex-wrap"
            style={{
              position: "relative",
              gap: "15px",
              rowGap: "8px",
              flexWrap: "wrap",
            }}
          >
            <ExportSiteCircuitsCSV ref={exportRef} />
            {selectedSite && (
              <>
                {isAdmin && (
                  <>
                    {showEmptyStructures && (
                      <Icon
                        name={"emptyStructure"}
                        onClick={() => handleHideEmptyStructures(false)}
                        overrides={{
                          disabled: isLoading,
                          tooltip: "Hide Empty Structures",
                          tooltipPlacement: "top",
                          iconColor: "#E02827",
                        }}
                        id="close-empty-icon"
                      />
                    )}
                    <Dropdown>
                      <Dropdown.Toggle
                        as="span"
                        style={{
                          cursor: "pointer",
                          fontSize: "1.2rem",
                          color: isLoading ? "gray" : "#0d6efd",
                        }}
                      >
                        <Icon
                          name={"admin"}
                          overrides={{
                            disabled: isLoading,
                            tooltip: "Admin Actions",
                            tooltipPlacement: "top",
                            size: "1.2rem",
                          }}
                          id="admin-icon"
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() =>
                            setShowEmptyStructures((prev) => !prev)
                          }
                        >
                          {showEmptyStructures && selectedEmptyStructure
                            ? "Discard Empty Structure Changes"
                            : showEmptyStructures
                              ? "Hide Empty Structures"
                              : "Show Empty Structures"}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setTempStructureModal({ show: true })}
                        >
                          Add Temporary Structure
                        </Dropdown.Item>
                        <Dropdown.Item onClick={handleShowCircuitModal}>
                          Move Circuits Between Sites
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </>
                )}
                <Icon
                  name={"siteMap"}
                  onClick={() =>
                    window.open(
                      `/sitemap?siteId=${selectedSite.siteId}`,
                      "_blank",
                    )
                  }
                  overrides={{
                    tooltipPlacement: "top",
                    size: "1.2rem",
                  }}
                  id="filter-icon"
                />
                <div
                  style={{
                    width: "24px",
                    textAlign: "center",
                    marginTop: filtering ? "4px" : "3px",
                  }}
                >
                  <Icon
                    name={filtering ? "removeFilter" : "filter"}
                    onClick={handleFilteringSelect}
                    overrides={{
                      disabled: isLoading,
                      tooltip: filtering ? "Remove Filter" : "Filter",
                      tooltipPlacement: "top",
                      size: "1.2rem",
                    }}
                    id="filter-icon"
                  />
                </div>
                <Dropdown>
                  <Dropdown.Toggle
                    as="span"
                    style={{
                      cursor: "pointer",
                      fontSize: "1.2rem",
                      color: isLoading ? "gray" : "#0d6efd",
                    }}
                  >
                    <Icon
                      name={"export"}
                      overrides={{
                        disabled: isLoading,
                        tooltip: "Export Site Details",
                        tooltipPlacement: "top",
                      }}
                      id="export-icon"
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => handleExport(isAdmin, selectedSite)}
                    >
                      Export Selected Site
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleExport(isAdmin, globalSitesData)}
                    >
                      Export All Sites
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                {isAdmin && (
                  <>
                    <Icon
                      name={"discard"}
                      onClick={() => setShowCancelModal((prev) => !prev)}
                      overrides={{
                        tooltip: "Discard Changes",
                        tooltipPlacement: "top",
                        disabled: !hasChanges,
                      }}
                      id="discard-icon"
                    />
                    <Icon
                      name={"save"}
                      onClick={handleSave}
                      overrides={{
                        tooltip: "Save Changes",
                        tooltipPlacement: "top",
                        disabled: !hasChanges,
                      }}
                      id="save-icon"
                    />
                  </>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
      <Row style={{ maxHeight: "4px" }}>
        <Col sm={8} />
        <Col>
          <Row>
            {validationError && (
              <span
                style={{
                  textAlign: "right",
                  color: "red",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {validationError}
              </span>
            )}
          </Row>
        </Col>
      </Row>
      <AddTempStructureModal
        modalData={tempStructureModal}
        setModalData={setTempStructureModal}
      />
      {showCircuitsModal && (
        <MoveCircuitsModal
          show={showCircuitsModal}
          setShow={setShowCircuitsModal}
        />
      )}
      <CancelModal
        show={showCancelModal}
        setShow={setShowCancelModal}
        cancelConfirm={resetSite}
      />
    </>
  );
};

export default ActionButtons;
